import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramCleanroomParticleCount } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';
import { ft2ToM2 } from '../../../utils/cleanroom';
import { C, FONT, SP } from '../../../theme/tokens';

const KEY = 'cleanroom.calc.particleCount.v1';

type Fields = {
  area: string;
  particleSize: string;
  isoClass: string;
  flowRate: string;
};

const EMPTY: Fields = {
  area: '',
  particleSize: '0.5',
  isoClass: '7',
  flowRate: '28.3',
};

const PARTICLE_SIZES = ['0.1', '0.2', '0.3', '0.5', '1', '5'];
const ISO_CLASSES = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// ── ISO 14644-1 calculations ──────────────────────────────────────────────────

/** Cn = 10^N × (0.1/D)^2.08 — class limit in particles/m³ */
function particleLimit(isoClass: number, sizeUm: number): number {
  const Cn = 10 ** isoClass * Math.pow(0.1 / sizeUm, 2.08);
  return Math.round(Number(Cn.toPrecision(3)));
}

/** Vs(min) = (20 / Cn) × 1000 liters, capped at 1000 L */
function minSampleVolumeL(isoClass: number, sizeUm: number): number {
  const Cn = particleLimit(isoClass, sizeUm);
  if (Cn <= 0) return 1000;
  const vol = (20 / Cn) * 1000;
  const rounded = Number(vol.toPrecision(3));
  return Math.min(1000, Math.max(1, Math.round(rounded)));
}

/** Legacy planning estimate: sqrt(A) for small rooms, ~27 per 1000 m² for large */
function sampleLocations(areaM2: number): number {
  if (areaM2 <= 0) return 0;
  if (areaM2 > 1000) return Math.ceil(27 * (areaM2 / 1000));
  return Math.max(1, Math.ceil(Math.sqrt(areaM2)));
}

/** Minimum sample time: ceil(max(Vs, 28.3) / flowRate) minutes */
function minSampleTimeMin(volumeL: number, flowRateLpm: number): number {
  const adjusted = Math.max(volumeL, 28.3);
  return Math.ceil(adjusted / flowRateLpm);
}

// ── Screen ────────────────────────────────────────────────────────────────────

export default function CleanroomParticleCountCalculatorScreen() {
  const [fields, setFields, { clear }] = usePersistedState<Fields>(KEY, EMPTY);
  const { unit, toImp } = useUnits();
  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const areaUnit = unit('area');

  const result = useMemo(() => {
    const areaFt2 = toImp(fields.area, 'area');
    const size = parseFloat(fields.particleSize);
    const klass = parseInt(fields.isoClass, 10);
    const flow = parseFloat(fields.flowRate);
    if (!Number.isFinite(areaFt2) || areaFt2 <= 0) return null;
    if (!Number.isFinite(size) || size <= 0) return null;
    if (!Number.isFinite(klass) || klass < 1 || klass > 9) return null;
    if (!Number.isFinite(flow) || flow <= 0) return null;

    const areaM2 = ft2ToM2(areaFt2);
    const Cn = particleLimit(klass, size);
    const vol = minSampleVolumeL(klass, size);
    const locs = sampleLocations(areaM2);
    const time = minSampleTimeMin(vol, flow);

    return { areaM2, Cn, vol, locs, time };
  }, [fields.area, fields.particleSize, fields.isoClass, fields.flowRate, toImp]);

  return (
    <CalcShell
      title="ISO 14644-1 Particle Count"
      subtitle="Cleanroom · Particle Counting"
      formula={
        <>
          <FormulaLine>Cn = 10^N × (0.1 / D)^2.08 (particles/m³)</FormulaLine>
          <FormulaLine>Vs(min) = (20 / Cn) × 1000 L</FormulaLine>
          <FormulaLine>NL ≈ ⌈√A⌉ (A ≤ 1000 m²) · ⌈27·A/1000⌉ (larger)</FormulaLine>
          <FormulaNote>
            N = ISO class (1–9){'\n'}
            D = particle size (µm){'\n'}
            A = cleanroom floor area (m²){'\n'}
            ISO 14644-1:2015 Annex A — field planning estimate
          </FormulaNote>
        </>
      }
      diagram={<DiagramCleanroomParticleCount />}
      onClear={() => { void clear(); }}
    >
      <NumericField
        label="Room Area"
        value={fields.area}
        onChangeText={(v) => set({ area: v })}
        rightText={areaUnit}
        maxDecimals={2}
      />

      <Text style={[styles.sectionLabel, { textTransform: 'none' }]}>PARTICLE SIZE (µm)</Text>
      <SegmentedButtons
        value={fields.particleSize}
        onValueChange={(v) => set({ particleSize: v })}
        buttons={PARTICLE_SIZES.map((s) => ({
          value: s,
          label: s === '1' ? '1.0' : s === '5' ? '5.0' : s,
        }))}
        style={styles.segmented}
      />

      <Text style={styles.sectionLabel}>ISO Class</Text>
      <SegmentedButtons
        value={fields.isoClass}
        onValueChange={(v) => set({ isoClass: v })}
        buttons={ISO_CLASSES.slice(0, 5).map((c) => ({ value: c, label: c }))}
        style={styles.segmented}
      />
      <SegmentedButtons
        value={ISO_CLASSES.slice(5).includes(fields.isoClass) ? fields.isoClass : ''}
        onValueChange={(v) => v && set({ isoClass: v })}
        buttons={ISO_CLASSES.slice(5).map((c) => ({ value: c, label: c }))}
        style={styles.segmented}
      />

      <Text style={styles.sectionLabel}>Flow Rate</Text>
      <SegmentedButtons
        value={fields.flowRate}
        onValueChange={(v) => set({ flowRate: v })}
        buttons={[
          { value: '28.3', label: '28.3 L/min' },
          { value: '100', label: '100 L/min' },
        ]}
        style={styles.segmented}
      />

      {result && (
        <>
          <Text style={[styles.sectionLabel, { marginTop: SP.md }]}>Results</Text>

          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Class Limit (ISO {fields.isoClass})</Text>
            <Text style={styles.resultValue}>
              {result.Cn.toLocaleString('en-US')} <Text style={styles.resultUnit}>particles/m³</Text>
            </Text>
            <Text style={styles.resultSub}>at {fields.particleSize} µm</Text>
          </View>

          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Min Sample Volume</Text>
            <Text style={styles.resultValue}>
              {result.vol} <Text style={styles.resultUnit}>L / location</Text>
            </Text>
          </View>

          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Sample Locations</Text>
            <Text style={styles.resultValue}>
              {result.locs} <Text style={styles.resultUnit}>locations</Text>
            </Text>
            <Text style={styles.resultSub}>for {result.areaM2.toFixed(1)} m² floor area</Text>
          </View>

          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Min Sample Time</Text>
            <Text style={styles.resultValue}>
              {result.time} <Text style={styles.resultUnit}>min / location</Text>
            </Text>
            <Text style={styles.resultSub}>at {fields.flowRate} L/min</Text>
          </View>

          <Text style={styles.note}>
            Planning estimate only. Compliance testing must follow the project test plan and ISO 14644-1:2015 Annex A Table A.1.
          </Text>
        </>
      )}
    </CalcShell>
  );
}

const styles = StyleSheet.create({
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: SP.md,
    marginBottom: SP.sm,
  },
  segmented: {
    marginBottom: 8,
  },
  resultCard: {
    backgroundColor: C.panel,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.edge,
    borderLeftWidth: 3,
    borderLeftColor: C.accent,
    paddingHorizontal: SP.md,
    paddingVertical: 10,
    marginBottom: SP.sm,
  },
  resultLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  resultValue: {
    fontFamily: FONT.mono,
    fontSize: 20,
    color: C.ink,
    fontWeight: '600',
  },
  resultUnit: {
    fontFamily: FONT.mono,
    fontSize: 13,
    color: C.dim,
    fontWeight: '400',
  },
  resultSub: {
    fontFamily: FONT.sans,
    fontSize: 11,
    color: C.dim,
    marginTop: 2,
  },
  note: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    lineHeight: 17,
    marginTop: SP.sm,
  },
});
